import { createContext, useContext, useMemo, type PropsWithChildren } from 'react';
import { I18nextProvider } from 'react-i18next';
import { useSyncExternalStore } from 'react';

import { i18n } from '../../i18n/config';
import { loadLocale, saveLocale, type AppLocale } from './localeStorage';

type LocaleContextValue = {
  locale: AppLocale;
  setLocale: (locale: AppLocale) => void;
};

const LocaleContext = createContext<LocaleContextValue | null>(null);

function subscribe(onStoreChange: () => void) {
  i18n.on('languageChanged', onStoreChange);
  return () => {
    i18n.off('languageChanged', onStoreChange);
  };
}

function getSnapshot(): AppLocale {
  return i18n.language === 'en' ? 'en' : 'uk';
}

export function LocaleProvider({ children }: PropsWithChildren) {
  const locale = useSyncExternalStore(subscribe, getSnapshot, loadLocale);

  const value = useMemo<LocaleContextValue>(
    () => ({
      locale,
      setLocale(nextLocale) {
        saveLocale(nextLocale);
        void i18n.changeLanguage(nextLocale);
      },
    }),
    [locale],
  );

  return (
    <I18nextProvider i18n={i18n}>
      <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>
    </I18nextProvider>
  );
}

export function useLocale() {
  const context = useContext(LocaleContext);
  if (!context) {
    return {
      locale: getSnapshot(),
      setLocale(nextLocale: AppLocale) {
        saveLocale(nextLocale);
        void i18n.changeLanguage(nextLocale);
      },
    };
  }
  return context;
}
